/* @flow */
import * as React from 'react';
import classnames from 'classnames/bind';
import styled from 'styled-components';
import { findDOMNode } from 'react-dom';
import { withRouter } from 'react-router';
import { createSelector } from 'reselect';
import type { Map } from 'immutable';
import { connect } from 'react-redux';
import createDebugger from 'debug';
import { compose } from 'ramda';
import { Field, reduxForm, type FormProps, formValueSelector } from 'redux-form/immutable';
import { CloseIcon } from 'trustar/icons/Icons';
import { Button, IconButton, IconButtonPure } from 'trustar-2.0/components/Button';
import {
  Icon,
  IconLookup,
  TooltipWrapper,
  DefaultTooltip,
} from 'trustar-2.0/components';

const debug = createDebugger('app:components:SearchBox'); // eslint-disable-line no-unused-vars

import s from './SearchBar.styl';
const cx = classnames.bind(s);
import * as Search from 'modules/search';
import * as Alerts from 'modules/alerts';

import { isReportsPage } from '../../utils/route';

const SearchIcon = ({ color = '#FFFFFF', ...props }: Object) => (
  <svg
    width='19px'
    height='19px'
    viewBox='0 0 19 19'
    version='1.1'
    xmlns='http://www.w3.org/2000/svg'
    xmlnsXlink='http://www.w3.org/1999/xlink'
    {...props}>
    <g
      stroke='none'
      strokeWidth='1'
      fill='none'
      fillRule='evenodd'
      transform='translate(-455.000000, -26.000000)'>
      <path
        d='M458.22475,38.735 C455.60175,36.113 455.60175,31.846 458.22475,29.224 C459.53675,27.914 461.25775,27.258 462.97975,27.258 C464.70175,27.258 466.42375,27.914 467.73575,29.224 C470.35775,31.846 470.35775,36.113 467.73575,38.735 C465.11275,41.356 460.84775,41.356 458.22475,38.735 M473.57575,43.686 L469.04675,39.158 C471.72475,36.024 471.58775,31.298 468.62475,28.334 C465.51275,25.222 460.44675,25.222 457.33475,28.334 C454.22175,31.447 454.22175,36.512 457.33475,39.626 C458.89075,41.181 460.93575,41.96 462.97975,41.96 C464.82675,41.96 466.66975,41.318 468.15775,40.047 L472.68575,44.576 C472.80875,44.699 472.96975,44.76 473.12975,44.76 C473.29075,44.76 473.45275,44.699 473.57575,44.576 C473.82175,44.33 473.82175,43.931 473.57575,43.686'
        fill={color}
      />
    </g>
  </svg>
);

const StyledDefaultTooltip = styled(DefaultTooltip)`
  display: inline-block;
  max-width: 350px;
  padding: 8px;
  background-color: ${({ theme }) => theme.ColorNames.THUNDER.hex};
`;

const FORM_NAME = 'search';

// The field name of the search value in the redux form
const SEARCH_KEY = 'searchText';

// Character limit for partial search. A wildcard search will be performed if the number of characters
// is greater than this limit. For more info, please refer to: https://trustar.atlassian.net/browse/EN-5217.
const NUMBER_OF_CHARACTER_LIMIT = 20;

type SearchBoxProps = FormProps & {
  clearSearch: () => void,
  palette: Object,
  location: Object,
  searchValue: string,
};

class SearchBox extends React.Component {
  props: SearchBoxProps;

  state = {
    focused: false,
    isInReports: isReportsPage((this.props.location || {}).pathname),
  }

  input: $TODO;

  componentDidUpdate(prevProps) {
    const { pathname: prevPathname = '' } = prevProps.location || {};
    const { pathname: crtPathname = '' } = this.props.location || {};

    if (prevPathname !== crtPathname) {
      this.setState({ isInReports: isReportsPage(crtPathname) });
    }
  }

  handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      this.onBlur(); // Hide shadow box
      // Clear and de-focus input field:
      e.target.value = '';
      e.target.blur();
    }
  }

  handleSubmit = (data) => {
    this.onBlur(); // clear shadow box
    /* $FlowIgnore possibly null value */
    document.activeElement.blur(); // De-focus search input
    this.props.handleSubmit(data);
  }

  onReset = (e) => {
    e.preventDefault(); // Not necessary if button[type='button'] is specified, but it's here to be safe
    this.onBlur(); // Hide shadow box
    this.props.reset(); // Reset form
    this.props.clearSearch(); // Reset search
  };

  onFocus = () => {
    this.setState({ focused: true });
  }

  onBlur = () => {
    this.setState({ focused: false });
  }

  focus = () => {
    if (this.input) {
      // $FlowIssue: The defs for findDOMNode are incorrect in this case
      const input: ?HTMLInputElement = findDOMNode(this.input);
      input && input.focus();
    }
  }

  render() {
    const { focused, isInReports } = this.state;
    const { pristine, palette, searchValue } = this.props;

    // NOTE: I used `formFocused` as a class name just in case 'focused' would
    // cause issues with the lower components that also get the focused class
    return (
      <div className={cx('SearchBoxContainer')} data-selector='OmnisearchBoxIII'>
        <form
          data-selector='OmnisearchBoxI'
          className={cx('SearchBox', { formFocused: focused })}
          onSubmit={this.handleSubmit}
        >
          <div className={cx('control-container')}>
            <Field
              type='text'
              name={SEARCH_KEY}
              component='input'
              id='@@_Search_Bar'
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onKeyDown={this.handleKeyDown}
              data-selector='OmnisearchBoxII'
              ref={(el) => { this.input = el; }}
              placeholder='Search by malware, IP address, email...'
            />
            {isInReports && searchValue && searchValue.length >= NUMBER_OF_CHARACTER_LIMIT && (
              <TooltipWrapper
                autohide
                forceOpen
                className={cx('wildcard-search-message-container')}
                id='search-bar__wildcard-search-message'
                placement='bottom'
                tooltip={(
                  <StyledDefaultTooltip>
                    Search terms longer than 20 characters will be performed with a wildcard automatically appended at the end of the search term for all report searches.
                    Indicator searches however will perform a full search irrespective of the length of the search term.
                    Click <a rel='noopener noreferrer' target='_blank' href='https://support.trustar.co/article/0f8zd89r7u-searching#using_long_search_terms'>here</a> for more details.
                  </StyledDefaultTooltip>
                )}
              >
                <Icon className={cx('icon')} icon={IconLookup.NOTIFICATION_CIRCLE} />
              </TooltipWrapper>
            )}
            {!pristine && (
              <IconButtonPure
                small
                strictStyle={false}
                fill='none'
                icon={IconLookup.CROSS2}
                type='button'
                className={cx('icon', 'close-icon')}
                onClick={this.onReset}
              />
            )}
          </div>
        </form>
        <div className={cx('searchButtonEnd')} onClick={this.handleSubmit}>
          <SearchIcon color={palette.iconFillWhite} />
        </div>
      </div>
    );
  }
}

const selector = formValueSelector(FORM_NAME);

export default compose(
  connect(
    state => ({
      palette: {
        logoFill: '#3568D5',
        iconFill: '#979BA6',
        iconFillWhite: '#fff',
      },
      searchValue: selector(state, SEARCH_KEY),
    }),
    dispatch => ({
      onSubmit(values: Map<string, string>) {
        const search = (values.get(SEARCH_KEY) || '').trim();
        if (search.length < 3) {
          const warning = 'Search term must be at least 3 characters long.';
          dispatch(Alerts.actions.error(warning));
        } else {
          dispatch(Search.actions.triggerSearch(search));
        }
      },
      clearSearch() {
        dispatch(Search.actions.resetSearch());
      },
    })
  ),
  reduxForm({
    form: FORM_NAME,
  }),
  withRouter,
)(SearchBox);



// WEBPACK FOOTER //
// ./src/app/components/search/SearchBar.js